<template>
  <div class="geral-tab-container">
    <template v-if="item">
      <!-- Type + Status -->
      <div class="status-tags">
        <Tag :value="itemTypeLabel(item.so_order_item_type)" severity="info" />
        <Tag
          v-if="item.item_display_status"
          :value="statusLabel(item.item_display_status)"
          :severity="statusSeverity(item.item_display_status)"
        />
      </div>

      <!-- Service / Plan / Priority -->
      <div class="info-grid">
        <KeyValueTag
          v-if="item.so_service_name"
          :label="t('Serviço')"
          :value="item.so_service_name"
        />
        <KeyValueTag
          v-if="item.so_priority_name"
          :label="t('Prioridade')"
          :value="item.so_priority_name"
        />
        <KeyValueTag
          v-if="item.so_order_item_qrcode"
          :label="t('QR Code')"
          :value="item.so_order_item_qrcode"
        />
        <KeyValueTag
          :label="t('Quantidade')"
          :value="String(item.so_order_item_quantity)"
        />
        <KeyValueTag
          :label="t('Valor unitario')"
          :value="currency(item.so_order_item_unit_price / 100)"
        />
        <KeyValueTag
          :label="t('Total')"
          :value="currency(item.so_order_item_total / 100)"
        />
      </div>

      <!-- Dates -->
      <div class="info-grid dates-grid">
        <KeyValueTag
          :label="t('Criado em')"
          :value="formatDateTime(new Date(item.so_order_item_created_at))"
        />
        <KeyValueTag
          v-if="item.so_order_item_expected_delivery_at"
          :label="t('Entrega prevista')"
          :value="formatDate(new Date(item.so_order_item_expected_delivery_at))"
        />
        <KeyValueTag
          v-if="item.so_order_item_delivered_at"
          :label="t('Entregue em')"
          :value="formatDateTime(new Date(item.so_order_item_delivered_at))"
        />
      </div>

      <!-- Attributes -->
      <div v-if="item.attributes && item.attributes.length > 0" class="attributes-container">
        <KeyValueTag
          v-for="attr in item.attributes"
          :key="attr.so_oiav_id"
          :label="attr.so_item_attribute_name"
          :value="formatAttrValue(attr)"
        />
      </div>

      <!-- Child items (additional_services, products) -->
      <div v-if="children.length > 0" class="children-section">
        <h3 class="children-title">{{ t('Itens relacionados') }}</h3>
        <div class="children-list">
          <div
            v-for="child in children"
            :key="child.so_order_item_id"
            class="child-item"
          >
            <Tag
              :value="itemTypeLabel(child.so_order_item_type)"
              size="small"
              severity="warn"
            />
            <span class="child-name">{{ child.so_service_name || child.so_order_item_description || `#${child.so_order_item_id}` }}</span>
            <span class="child-total">
              {{ currency(child.so_order_item_total / 100) }}
            </span>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="not-found">
      {{ t('Item não encontrado') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useDateFormatter } from '../../../composables/useDateFormatter';
import type { Orders_ItemRow } from '../../../contracts/Orders_ItemRow';
import type { Orders_OiavRow } from '../../../contracts/Orders_OiavRow';

const { t, n } = useI18n();
const { formatDateTime, formatDate } = useDateFormatter();

const props = defineProps<{
  item: Orders_ItemRow | null;
  children: Orders_ItemRow[];
}>();

function currency(value: number): string {
  return n(value, 'currency');
}

function formatAttrValue(attr: Orders_OiavRow): string {
  if (attr.so_item_attribute_type === 'boolean') {
    return attr.so_oiav_value === '1' ? t('Sim') : t('Não');
  }
  return attr.so_oiav_value;
}

function itemTypeLabel(type: string): string {
  switch (type) {
    case 'service': return t('Serviço');
    case 'product': return t('Produto');
    case 'additional_service': return t('Serviço adicional');
    case 'combo': return t('Combo');
    default: return type;
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'aguardando_laudo_entrada': return t('Aguardando laudo de entrada');
    case 'em_producao': return t('Em produção');
    case 'aguardando_laudo_saida': return t('Aguardando laudo de saída');
    case 'pronto': return t('Pronto');
    case 'entregue': return t('Entregue');
    default: return status;
  }
}

function statusSeverity(status: string): string {
  switch (status) {
    case 'entregue': return 'success';
    case 'pronto': return 'info';
    case 'em_producao': return 'warn';
    case 'aguardando_laudo_entrada': return 'warn';
    case 'aguardando_laudo_saida': return 'warn';
    default: return 'contrast';
  }
}

function loadData() {
  // Prop-driven; data already loaded at modal level
}

defineExpose({ loadData });
</script>

<style scoped>
.geral-tab-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.status-tags {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.dates-grid {
  margin-top: 0.5rem;
}

.attributes-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.children-section {
  margin-top: 1rem;
}

.children-title {
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: var(--p-text-color);
}

.children-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.child-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0;
  border-bottom: 1px solid var(--p-content-border-color);
}

.child-item:last-child {
  border-bottom: none;
}

.child-name {
  font-size: 0.875rem;
  flex: 1;
}

.child-total {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}

.not-found {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
  padding: 0.5rem 0;
}
</style>
